import { apiFetch } from "@/lib/api";

import { miscQuickBooksTimePrefersBrowserGeolocation } from "./miscQuickBooksTimeGeolocationPreference";

export const MISC_QBT_CLOCK_STATUS_QUERY_KEY = ["personal", "quickbooks-time", "status"] as const;

export const MISC_QBT_DEVICE_IDENTIFIER_STORAGE_KEY = "personal-qbt-device-identifier";

export interface MiscQuickBooksTimeClockStatus {
  oauthConnected: boolean;
  quickbooksTimeUserId: number | null;
  quickbooksTimeUserMatched: boolean;
  workJobcodeId: number;
  lunchJobcodeId: number;
  lunchJobcodeConfigured: boolean;
  onTheClock: boolean;
  onLunch: boolean;
  onBreak: boolean;
  openTimesheetId: number | null;
  openLunchTimesheetId: number | null;
  clockIn: string | null;
  lunchStartedAt: string | null;
  breakStartedAt: string | null;
  calendarDate: string;
  timeZone: string;
}

export interface MiscQuickBooksTimeClockLocationPayload {
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  deviceIdentifier?: string;
  originHint?: string;
}

export function miscQuickBooksTimeDeviceIdentifier(): string {
  const existing = localStorage.getItem(MISC_QBT_DEVICE_IDENTIFIER_STORAGE_KEY);
  if (existing && existing.trim() !== "") {
    return existing.trim();
  }
  const created =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `hub-pc-${String(Date.now())}`;
  localStorage.setItem(MISC_QBT_DEVICE_IDENTIFIER_STORAGE_KEY, created);
  return created;
}

export async function readMiscQuickBooksTimeClockLocation(): Promise<MiscQuickBooksTimeClockLocationPayload> {
  const deviceIdentifier = miscQuickBooksTimeDeviceIdentifier();
  const payload: MiscQuickBooksTimeClockLocationPayload = {
    deviceIdentifier,
    originHint: deviceIdentifier,
  };

  if (!miscQuickBooksTimePrefersBrowserGeolocation()) {
    return payload;
  }

  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 12_000,
        maximumAge: 60_000,
      });
    });
    payload.latitude = position.coords.latitude;
    payload.longitude = position.coords.longitude;
    payload.accuracy = position.coords.accuracy;
  } catch {
    // Site-fixed coordinates are applied server-side when browser GPS is unavailable.
  }

  return payload;
}

export async function fetchMiscQuickBooksTimeClockStatus(): Promise<MiscQuickBooksTimeClockStatus> {
  const response = await apiFetch<{ status: MiscQuickBooksTimeClockStatus }>(
    "/api/personal/quickbooks-time/status",
  );
  return response.status;
}

export async function postMiscQuickBooksTimeClockIn(
  location: MiscQuickBooksTimeClockLocationPayload,
): Promise<MiscQuickBooksTimeClockStatus> {
  const response = await apiFetch<{ status: MiscQuickBooksTimeClockStatus }>(
    "/api/personal/quickbooks-time/clock-in",
    {
      method: "POST",
      body: JSON.stringify(location),
    },
  );
  return response.status;
}

export async function postMiscQuickBooksTimeClockOut(
  location: MiscQuickBooksTimeClockLocationPayload,
): Promise<MiscQuickBooksTimeClockStatus> {
  const response = await apiFetch<{ status: MiscQuickBooksTimeClockStatus }>(
    "/api/personal/quickbooks-time/clock-out",
    {
      method: "POST",
      body: JSON.stringify(location),
    },
  );
  return response.status;
}

export async function postMiscQuickBooksTimeBreakStart(): Promise<MiscQuickBooksTimeClockStatus> {
  const response = await apiFetch<{ status: MiscQuickBooksTimeClockStatus }>(
    "/api/personal/quickbooks-time/break/start",
    { method: "POST" },
  );
  return response.status;
}

export async function postMiscQuickBooksTimeBreakEnd(): Promise<MiscQuickBooksTimeClockStatus> {
  const response = await apiFetch<{ status: MiscQuickBooksTimeClockStatus }>(
    "/api/personal/quickbooks-time/break/end",
    { method: "POST" },
  );
  return response.status;
}

export async function postMiscQuickBooksTimeLunchStart(
  location: MiscQuickBooksTimeClockLocationPayload,
): Promise<MiscQuickBooksTimeClockStatus> {
  const response = await apiFetch<{ status: MiscQuickBooksTimeClockStatus }>(
    "/api/personal/quickbooks-time/lunch/start",
    {
      method: "POST",
      body: JSON.stringify(location),
    },
  );
  return response.status;
}

export async function postMiscQuickBooksTimeLunchEnd(
  location: MiscQuickBooksTimeClockLocationPayload,
): Promise<MiscQuickBooksTimeClockStatus> {
  const response = await apiFetch<{ status: MiscQuickBooksTimeClockStatus }>(
    "/api/personal/quickbooks-time/lunch/end",
    {
      method: "POST",
      body: JSON.stringify(location),
    },
  );
  return response.status;
}

export function formatMiscQuickBooksTimeInstant(value: string | null): string {
  if (value == null || value.trim() === "") {
    return "—";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  });
}
